import { useState } from "react";
import type { OpenHoldItem } from "../types.js";
import "./styles.css";

export interface BlockerPanelProps {
  /**
   * The open blockers from the context response. Each carries its declared label, message,
   * and which actions apply (can_receive, can_waive).
   */
  blockers: readonly OpenHoldItem[];
  /**
   * Called when the user settles a blocker. The panel disables the settling blocker until the
   * caller returns, then invokes onSettled to re-read /context.
   *
   * Returns true on success (item settled), false on failure (show error inline).
   */
  onSettle: (
    item: OpenHoldItem,
    decision: "received" | "not_required",
    fields: { note?: string; received_date?: string; reason?: string },
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
}

interface BlockerState {
  /** Which action is being prepared — not yet sent. */
  action: "received" | "not_required" | null;
  /** Optional note for "Received". */
  note: string;
  /** Optional date for "Received" (calendar date string). */
  receivedDate: string;
  /** Required reason for "Not required". */
  reason: string;
  /** Error message from a failed settle attempt. */
  error: string | null;
  /** True while the settle request is in flight. */
  settling: boolean;
}

const INITIAL_STATE: BlockerState = {
  action: null,
  note: "",
  receivedDate: "",
  reason: "",
  error: null,
  settling: false,
};

/**
 * Phase 572 wave 3 task 3.3 — the approval page's blockers, with their two actions.
 *
 * Each blocker renders its declared label + declared message with only the actions the server
 * said apply: "Received" (optional date + optional note) and "Not required" (reason required,
 * submit disabled until non-blank). No raw item key, form id or status value reaches the screen.
 *
 * Settling an item calls the settle endpoint, then re-reads /context so the settled item
 * disappears and the decision becomes available in place.
 */
export function BlockerPanel({ blockers, onSettle }: BlockerPanelProps) {
  // Each blocker has its own state for action selection and form fields.
  const [states, setStates] = useState<Record<string, BlockerState>>({});

  function stateFor(item: OpenHoldItem): BlockerState {
    const key = `${item.form_id}:${item.item_key}:${item.iteration_key}`;
    return states[key] ?? INITIAL_STATE;
  }

  function updateState(item: OpenHoldItem, patch: Partial<BlockerState>) {
    const key = `${item.form_id}:${item.item_key}:${item.iteration_key}`;
    setStates((s) => ({ ...s, [key]: { ...stateFor(item), ...patch } }));
  }

  function selectAction(item: OpenHoldItem, action: "received" | "not_required" | null) {
    updateState(item, { action, error: null });
  }

  async function settle(item: OpenHoldItem) {
    const state = stateFor(item);
    if (state.action === null || state.settling) return;

    // Validate reason is non-blank for "not_required".
    if (state.action === "not_required" && state.reason.trim().length === 0) {
      updateState(item, { error: "Enter a reason before marking this item as not required." });
      return;
    }

    updateState(item, { settling: true, error: null });

    const result = await onSettle(item, state.action, {
      note: state.note.trim() || undefined,
      received_date: state.receivedDate.trim() || undefined,
      reason: state.reason.trim() || undefined,
    });

    if (!result.ok) {
      updateState(item, { settling: false, error: result.message });
    }
    // On success, onSettle triggers a context reload which will remove this blocker from the list.
  }

  if (blockers.length === 0) return null;

  return (
    <div className="blocker-panel">
      <h3 className="blocker-panel-heading">Items to settle before approving</h3>
      <p className="blocker-panel-intro">
        These items are holding this approval. Settle each one to continue.
      </p>
      <ul className="blocker-list">
        {blockers.map((item) => {
          const state = stateFor(item);
          const key = `${item.form_id}:${item.item_key}:${item.iteration_key}`;
          return (
            <li key={key} className="blocker-item">
              <div className="blocker-header">
                <span className="blocker-label">{item.label}</span>
              </div>
              <p className="blocker-message">{item.message}</p>

              {state.error && (
                <div className="blocker-error" role="alert">
                  {state.error}
                </div>
              )}

              <div className="blocker-actions">
                {item.can_receive && (
                  <button
                    type="button"
                    className={`blocker-action-button${state.action === "received" ? " blocker-action-selected" : ""}`}
                    onClick={() => selectAction(item, state.action === "received" ? null : "received")}
                    disabled={state.settling}
                  >
                    Received
                  </button>
                )}
                {item.can_waive && (
                  <button
                    type="button"
                    className={`blocker-action-button${state.action === "not_required" ? " blocker-action-selected" : ""}`}
                    onClick={() => selectAction(item, state.action === "not_required" ? null : "not_required")}
                    disabled={state.settling}
                  >
                    Not required
                  </button>
                )}
              </div>

              {state.action === "received" && (
                <div className="blocker-form">
                  <div className="blocker-form-field">
                    <label htmlFor={`${key}-date`} className="blocker-form-label">
                      Date received <span className="blocker-form-optional">(optional)</span>
                    </label>
                    <input
                      type="date"
                      id={`${key}-date`}
                      className="blocker-form-input"
                      value={state.receivedDate}
                      onChange={(e) => updateState(item, { receivedDate: e.target.value })}
                      disabled={state.settling}
                    />
                  </div>
                  <div className="blocker-form-field">
                    <label htmlFor={`${key}-note`} className="blocker-form-label">
                      Note <span className="blocker-form-optional">(optional)</span>
                    </label>
                    <textarea
                      id={`${key}-note`}
                      className="blocker-form-textarea"
                      value={state.note}
                      onChange={(e) => updateState(item, { note: e.target.value })}
                      disabled={state.settling}
                      placeholder="Any additional details..."
                      rows={2}
                    />
                  </div>
                  <button
                    type="button"
                    className="button-primary blocker-submit"
                    onClick={() => settle(item)}
                    disabled={state.settling}
                  >
                    {state.settling ? "Settling..." : "Mark as received"}
                  </button>
                </div>
              )}

              {state.action === "not_required" && (
                <div className="blocker-form">
                  <div className="blocker-form-field">
                    <label htmlFor={`${key}-reason`} className="blocker-form-label">
                      Reason <span className="blocker-form-required">*</span>
                    </label>
                    <textarea
                      id={`${key}-reason`}
                      className="blocker-form-textarea"
                      value={state.reason}
                      onChange={(e) => updateState(item, { reason: e.target.value, error: null })}
                      disabled={state.settling}
                      placeholder="Why is this item not required?"
                      rows={2}
                      aria-required="true"
                    />
                  </div>
                  <button
                    type="button"
                    className="button-primary blocker-submit"
                    onClick={() => settle(item)}
                    disabled={state.settling || state.reason.trim().length === 0}
                  >
                    {state.settling ? "Settling..." : "Mark as not required"}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
